<template>
  <AuthLayout :max-width="640">
    <v-card-title class="mb-4">Cég regisztráció</v-card-title>

    <v-alert v-if="tokenError" type="error" variant="tonal" class="mb-4">
      Ez a meghívó link érvénytelen vagy már felhasználták.
    </v-alert>

    <v-alert v-if="successMessage" type="success" variant="tonal" class="mb-4">
      {{ successMessage }}
    </v-alert>

    <v-form
      ref="formRef"
      v-if="!tokenError && !successMessage"
      @submit.prevent="onSubmit"
    >
      <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
        {{ errorMessage }}
      </v-alert>

      <div class="text-subtitle-1 font-weight-bold mb-2">Cég adatok</div>

      <v-text-field
        v-model="form.companyName"
        label="Cégnév"
        variant="outlined"
        density="compact"
        class="mb-2"
        :rules="[rules.required]"
      />
      <v-text-field
        v-model="form.address"
        label="Cím"
        variant="outlined"
        density="compact"
        class="mb-2"
      />
      <v-text-field
        v-model="form.phone"
        label="Telefonszám"
        variant="outlined"
        density="compact"
        class="mb-2"
      />
      <v-text-field
        v-model="form.email"
        label="Cég email"
        variant="outlined"
        density="compact"
        class="mb-2"
        :rules="[rules.optionalEmail]"
      />
      <v-text-field
        v-model="form.taxNumber"
        label="Adószám"
        variant="outlined"
        density="compact"
        class="mb-2"
      />
      <v-text-field
        v-model="form.bankAccount"
        label="Bankszámlaszám"
        variant="outlined"
        density="compact"
        class="mb-2"
      />
      <v-text-field
        v-model="form.bankName"
        label="Bank neve"
        variant="outlined"
        density="compact"
        class="mb-2"
      />
      <v-text-field
        v-model="form.billingAddress"
        label="Számlázási cím"
        variant="outlined"
        density="compact"
        class="mb-4"
      />

      <div class="text-subtitle-1 font-weight-bold mb-2">
        Adminisztrátor adatok
      </div>

      <v-text-field
        v-model="form.adminLastName"
        label="Vezetéknév"
        variant="outlined"
        density="compact"
        class="mb-2"
        :rules="[rules.required]"
      />
      <v-text-field
        v-model="form.adminFirstName"
        label="Keresztnév"
        variant="outlined"
        density="compact"
        class="mb-2"
        :rules="[rules.required]"
      />
      <v-text-field
        v-model="form.adminEmail"
        label="Email"
        variant="outlined"
        density="compact"
        class="mb-2"
        :rules="[rules.required, rules.email]"
      />
      <v-text-field
        v-model="form.adminPassword"
        label="Jelszó"
        :type="showPassword ? 'text' : 'password'"
        :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
        variant="outlined"
        density="compact"
        class="mb-2"
        :rules="[rules.required, rules.minPassword]"
        @click:append-inner="showPassword = !showPassword"
      />
      <v-text-field
        v-model="form.adminPasswordConfirm"
        label="Jelszó megerősítése"
        :type="showPassword ? 'text' : 'password'"
        variant="outlined"
        density="compact"
        class="mb-4"
        :rules="[rules.required, rules.passwordMatch]"
      />

      <v-btn
        type="submit"
        color="primary"
        size="large"
        block
        :loading="loading"
        :disabled="loading"
      >
        Regisztráció
      </v-btn>
    </v-form>
  </AuthLayout>
</template>

<script setup lang="ts">
import AuthLayout from "@/components/layout/AuthLayout.vue";
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { InvitationService } from "@/services/InvitationService";

const route = useRoute();
const invitationService = new InvitationService();

const token = route.query.token as string;
const tokenError = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const loading = ref(false);
const showPassword = ref(false);
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(
  null
);

const rules = {
  required: (v: string) => !!v?.trim() || "Kötelező mező",
  email: (v: string) => /.+@.+\..+/.test(v) || "Érvényes email cím szükséges",
  optionalEmail: (v: string) =>
    !v || /.+@.+\..+/.test(v) || "Érvényes email cím szükséges",
  minPassword: (v: string) =>
    v?.length >= 8 || "A jelszónak legalább 8 karakter hosszúnak kell lennie",
  passwordMatch: (v: string) =>
    v === form.value.adminPassword || "A jelszavak nem egyeznek",
};

const form = ref({
  companyName: "",
  address: "",
  phone: "",
  email: "",
  taxNumber: "",
  bankAccount: "",
  bankName: "",
  billingAddress: "",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  adminPassword: "",
  adminPasswordConfirm: "",
});

onMounted(async () => {
  if (!token) {
    tokenError.value = true;
    return;
  }
  try {
    await invitationService.validateToken(token);
  } catch {
    tokenError.value = true;
  }
});

const onSubmit = async () => {
  const { valid } = await formRef.value!.validate();
  if (!valid) return;

  errorMessage.value = "";
  loading.value = true;
  try {
    await invitationService.register({ token, ...form.value });
    successMessage.value =
      "Sikeres regisztráció! Kérjük, erősítse meg email címét a beküldött levélben található linkre kattintva.";
  } catch (e) {
    if (e?.response?.status === 409) {
      errorMessage.value =
        "Ez az email cím már regisztrálva van a rendszerben.";
    } else {
      errorMessage.value = "A regisztráció sikertelen. Kérjük, próbálja újra.";
    }
  } finally {
    loading.value = false;
  }
};
</script>
